import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './OaraAgentPanel.css';

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  role: ChatRole;
  content: string;
}

const OaraAgentPanel: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: 'Hi, I am OARA Agent. Ask me about students, attendance, camps, billing, or admin workflows.'
    }
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = useMemo(
    () => localStorage.getItem('cognitoToken') || localStorage.getItem('token') || '',
    []
  );

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) {
      return;
    }

    const nextUserMessage: ChatMessage = { role: 'user', content: trimmed };
    const nextHistory = [...messages, nextUserMessage];

    setMessages(nextHistory);
    setInput('');
    setError(null);
    setIsSending(true);

    try {
      const response = await axios.post(
        `${config.BACKEND_URL}/api/agents/oara/chat`,
        {
          message: trimmed,
          history: messages.map((m) => ({ role: m.role, content: m.content }))
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const answer = response?.data?.answer || 'No response received from OARA Agent.';
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to contact OARA Agent.';
      setError(detail);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'I could not complete that request right now. Please try again.'
        }
      ]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="oara-agent-panel">
      <div className="oara-agent-header">
        <h3>OARA Agent</h3>
        <p>Admin assistant powered by AWS Bedrock</p>
      </div>

      <div className="oara-agent-chat-window">
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={`oara-agent-message ${message.role === 'user' ? 'user' : 'assistant'}`}
          >
            <div className="oara-agent-message-role">{message.role === 'user' ? 'You' : 'OARA Agent'}</div>
            <div className="oara-agent-message-content">{message.content}</div>
          </div>
        ))}
        {isSending && (
          <div className="oara-agent-message assistant">
            <div className="oara-agent-message-role">OARA Agent</div>
            <div className="oara-agent-message-content">Thinking...</div>
          </div>
        )}
      </div>

      {error && <div className="oara-agent-error">{error}</div>}

      <div className="oara-agent-input-row">
        <textarea
          className="oara-agent-input"
          rows={3}
          placeholder="Ask OARA Agent..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <button className="btn btn-primary" onClick={sendMessage} disabled={isSending || !input.trim()}>
          {isSending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default OaraAgentPanel;
